/** PROBATION-REVIEW-001 P2.1 — Employee Workspace "Probation" tab (compact, animated). */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Hourglass, UserCheck, ListChecks, ShieldCheck, AlertTriangle } from 'lucide-react';
import { SectionCard } from '../workspace/OverviewCards';
import { ProbationPill, StatusPill, Segmented, ProgressStrip, REVIEW_STATUS, deriveProbation, fmtDate } from './probationUi';
import ReviewDrawer from './ReviewDrawer';
import DecisionDialog from './DecisionDialog';
import RemindersRow from './RemindersRow';

const API = process.env.REACT_APP_BACKEND_URL || '';
const h = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const err = (e) => e?.response?.data?.detail?.message || (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : null) || e?.message || 'Request failed';
const FLAG_LABEL = { reviewer_missing: 'No Reporting Manager', reviewer_unlinked: 'Manager has no login', reviewer_inactive: 'Manager not employed', team_changed: 'Team changed — confirm manager' };
const currentUserId = () => { try { return JSON.parse(localStorage.getItem('user') || '{}')?.id; } catch { return null; } };
const addMonths = (iso, m) => { if (!iso) return null; const d = new Date(`${iso}T00:00:00`); const day = d.getDate(); d.setDate(1); d.setMonth(d.getMonth() + m); d.setDate(Math.min(day, new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate())); return d.toISOString().slice(0, 10); };

export default function ProbationTab({ employeeId, readOnly, onChanged }) {
  const [data, setData] = useState(null);
  const [managers, setManagers] = useState([]);
  const [suggest, setSuggest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reviewing, setReviewing] = useState(null);
  const [deciding, setDeciding] = useState(null);

  const load = useCallback(async () => {
    const [p, m, s] = await Promise.all([
      axios.get(`${API}/api/hr/probation/employees/${employeeId}`, { headers: h() }),
      axios.get(`${API}/api/hr/options/managers`, { headers: h(), params: { exclude: employeeId } }),
      axios.get(`${API}/api/hr/probation/employees/${employeeId}/reviewer-suggestion`, { headers: h() }).catch(() => ({ data: null })),
    ]);
    setData(p.data); setManagers(m.data.managers || []); setSuggest(s.data);
  }, [employeeId]);
  useEffect(() => { load().catch(e => toast.error(err(e))); }, [load]);

  const act = async (fn, ok) => {
    try { setBusy(true); const r = await fn(); if (r?.data) setData(r.data); await load(); onChanged?.(); toast.success(ok, { duration: 3500 }); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  if (!data) return <div className="lv-card p-4 text-sm text-slate-500" data-testid="probation-loading">Loading probation…</div>;
  const p = deriveProbation(data.probation);
  const canManage = !readOnly && data.capabilities?.includes('manage');
  const terminal = p && ['confirmed', 'closed'].includes(p.status);
  const needsVerify = p && !terminal && !p.verified;
  const canApprove = !readOnly && data.capabilities?.includes('approve');
  const canReview = (r) => (r.reviewer_user_id === currentUserId() || data.capabilities?.includes('review')) && p?.verified && ['open', 'returned'].includes(r.runtime_status || r.status) && !readOnly;
  const atLimit = p && !terminal && (p.extensions_count || 0) >= (p.max_extensions || 2);

  return (
    <div className="space-y-3">
      <SectionCard icon={Hourglass} title="Probation" subtitle={p ? `${fmtDate(p.start_date)} → ${fmtDate(p.end_date)}` : 'Not tracked for this employee'} testid="ws-probation" className="!p-4"
        action={p && <span key={p.status} className="prb-swap"><ProbationPill probation={p} testid="probation-status-pill" /></span>}>
        {p ? (
          <div className="space-y-3">
            <ProgressStrip start={p.start_date} end={p.end_date} status={p.status} />
            <div className="prb-metrics" data-testid="probation-summary">
              <Metric label="Days remaining" value={p.days_remaining === null ? '—' : p.days_remaining < 0 ? `${-p.days_remaining} overdue` : p.days_remaining} tone={p.days_remaining < 0 ? 'rose' : p.days_remaining <= 14 ? 'amber' : 'blue'} />
              <Metric label="Period" value={p.period_months ? `${p.period_months} months` : 'Custom'} sub={p.extensions_count ? `extended ×${p.extensions_count}` : null} />
              <Metric label="Verification" value={p.verified ? 'Verified' : 'Pending HR'} sub={p.verified ? p.verified_by_name : 'end date + manager'} tone={p.verified ? 'emerald' : 'slate'} />
              <Metric label="Reminders" value={p.notifications_armed ? 'Armed' : 'Not armed'} sub={p.notify_mode === 'disabled' ? 'delivery disabled' : null} tone={p.notifications_armed ? 'violet' : 'slate'} />
            </div>
            {(p.reviewer_flags?.length > 0 || (needsVerify && canManage)) && (
              <div className="prb-banner prb-reveal" data-testid="probation-verify-banner">
                <ShieldCheck className="h-4 w-4 text-violet-600 shrink-0" />
                <div className="flex flex-wrap items-center gap-1.5 min-w-0 flex-1">
                  {needsVerify && <span className="text-[12.5px] text-slate-700">HR verification required before reminders arm</span>}
                  {p.reviewer_flags?.map(f => <span key={f} className="prb-flag" data-testid="probation-flag"><AlertTriangle className="h-3 w-3" />{FLAG_LABEL[f] || f}</span>)}
                </div>
                {needsVerify && canManage && (
                  <button type="button" className="prb-btn-primary !h-8 !px-3.5 !text-[12.5px]" disabled={busy || p.reviewer_flags?.length > 0} data-testid="probation-verify-btn"
                    onClick={() => act(() => axios.post(`${API}/api/hr/probation/employees/${employeeId}/verify`, {}, { headers: h() }), 'Probation verified')}>Verify</button>
                )}
              </div>
            )}
          </div>
        ) : <p className="text-[13px] text-slate-500">Start tracking to schedule the manager review and HR confirmation.</p>}
        {p && <RemindersRow employeeId={employeeId} refreshKey={p.status + (p.end_date || '')} />}
        {canManage && !terminal && !p && !data.employee.date_of_joining && (
          <p className="prb-banner mt-3 text-[12.5px] text-amber-800" data-testid="probation-doj-missing">No date of joining on record — set it once under <strong>Basic</strong> details (Admin / HR Manager) before starting probation tracking. Nothing is calculated automatically.</p>
        )}
        {canManage && !terminal && (p || data.employee.date_of_joining) && <SetupForm employeeId={employeeId} p={p} start={p?.start_date || data.employee.date_of_joining} busy={busy} act={act} />}
      </SectionCard>

      <ReviewerCard p={p} data={data} managers={managers} suggest={suggest} canManage={canManage && !terminal} busy={busy} act={act} employeeId={employeeId} />

      {data.reviews?.length > 0 && (
        <SectionCard icon={ListChecks} title="Review cycles" subtitle="Manager recommendation and HR decision per cycle" testid="ws-probation-cycles" delay={120} className="!p-4">
          <ol className="prb-timeline">
            {[...data.reviews].reverse().map((r, i) => {
              const st = r.runtime_status || r.status;
              return (
                <li key={r.id} className="prb-tl-item" style={{ animationDelay: `${i * 60}ms` }} data-testid={`probation-cycle-${r.cycle_no}`}>
                  <span className={`prb-tl-dot ${(REVIEW_STATUS[st] || {}).cls || 'prb-slate'}`} />
                  <div className="prb-tl-body">
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold text-slate-800 truncate">Cycle {r.cycle_no} · {r.review_type === 'extension' ? 'Extension' : 'Initial'} <span className="font-medium text-slate-400">· due {fmtDate(r.due_date)}</span></p>
                      <p className="text-[12px] text-slate-500 truncate">
                        {r.reviewer_name ? `${r.reviewer_name}${r.reviewer_user_id ? '' : ' (no login)'}` : r.reviewer_employee_id || 'No reviewer'}
                        {r.recommendation ? ` · recommends ${r.recommendation.outcome === 'extended' ? `extension to ${fmtDate(r.recommendation.extension?.revised_end_date)}` : 'confirmation'}` : ''}
                        {r.hr_decision ? ` · HR ${r.hr_decision.decision} by ${r.hr_decision.decided_by_name}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span key={st} className="prb-swap"><StatusPill map={REVIEW_STATUS} status={st} testid={`probation-cycle-status-${r.cycle_no}`} /></span>
                      {canReview(r) && <button type="button" className="prb-btn-primary !h-7 !px-3 !text-[12px]" onClick={() => setReviewing(r)} data-testid={`probation-cycle-review-${r.cycle_no}`}>{st === 'returned' ? 'Revise' : 'Review'}</button>}
                      {st === 'submitted' && canApprove && <button type="button" className="prb-btn-primary !h-7 !px-3 !text-[12px]" onClick={() => setDeciding(r)} data-testid={`probation-cycle-decide-${r.cycle_no}`}>Decide</button>}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
          {atLimit && canApprove && <OverrideRow employeeId={employeeId} p={p} busy={busy} act={act} />}
        </SectionCard>
      )}
      <ReviewDrawer open={!!reviewing} onOpenChange={(o) => !o && setReviewing(null)} review={reviewing} employee={data.employee} probation={p} onSubmitted={() => load().then(onChanged)} />
      <DecisionDialog open={!!deciding} onOpenChange={(o) => !o && setDeciding(null)} review={deciding} employee={data.employee} onDecided={() => load().then(onChanged)} />
    </div>
  );
}

function OverrideRow({ employeeId, p, busy, act }) {
  const [open, setOpen] = useState(false);
  const [just, setJust] = useState('');
  if (!open) return <div className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2" data-testid="probation-override-row">
    <p className="text-[12px] text-slate-600">Extension limit reached ({p.extensions_count}/{p.max_extensions}). Further extensions need an HR Manager override.</p>
    <button type="button" className="prb-btn-ghost !h-7 !text-[12px]" onClick={() => setOpen(true)} data-testid="probation-override-btn">Allow one more</button></div>;
  return <div className="mt-3 flex flex-wrap items-end gap-2 prb-reveal" data-testid="probation-override-form">
    <div className="flex-1 min-w-[220px]"><p className="prb-metric-label mb-1">Override justification <span className="normal-case tracking-normal text-slate-400">(min 20)</span></p>
      <input className="prb-input" value={just} onChange={e => setJust(e.target.value)} data-testid="probation-override-justification" /></div>
    <button type="button" className="prb-btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
    <button type="button" className="prb-btn-primary" disabled={busy || just.trim().length < 20} data-testid="probation-override-save"
      onClick={() => act(() => axios.post(`${API}/api/hr/probation/employees/${employeeId}/extension-override`, { justification: just.trim() }, { headers: h() }), 'Extension override recorded').then(() => setOpen(false))}>Record override</button>
  </div>;
}

const Metric = ({ label, value, sub, tone = 'slate' }) => (
  <div className={`prb-metric tone-${tone}`}>
    <p className="prb-metric-label">{label}</p>
    <p className="prb-metric-value prb-swap" key={String(value)}>{value}</p>
    {sub && <p className="prb-metric-sub truncate">{sub}</p>}
  </div>
);

function SetupForm({ employeeId, p, start, busy, act }) {
  const [open, setOpen] = useState(!p);
  const [mode, setMode] = useState(p?.period_months ? String(p.period_months) : p ? 'custom' : '6');
  const [custom, setCustom] = useState(p?.period_months ? '' : (p?.end_date || ''));
  const [reason, setReason] = useState('');
  const [confirmPast, setConfirmPast] = useState(false); // P6 B2 — explicit HR confirmation for an already-ended period
  useEffect(() => { setOpen(!p); }, [p]);
  const end = mode === 'custom' ? custom : addMonths(start, Number(mode));
  const past = end && end < new Date().toISOString().slice(0, 10);
  useEffect(() => { if (!past) setConfirmPast(false); }, [past]);
  const submit = () => {
    const body = mode === 'custom' ? { custom_end_date: custom } : { period_months: Number(mode) };
    if (p) body.reason = reason.trim() || 'Probation dates adjusted by HR';
    if (past) body.allow_past = true;
    act(() => axios.put(`${API}/api/hr/probation/employees/${employeeId}/tracking`, body, { headers: h() }), p ? 'Probation updated' : (past ? 'Overdue probation review opened' : 'Probation tracking started')).then(() => setOpen(false));
  };
  if (!open) return <div className="mt-3 flex justify-end"><button type="button" className="prb-btn-ghost" data-testid="probation-adjust-btn" onClick={() => setOpen(true)}>Adjust dates</button></div>;
  return (
    <div className="mt-3 pt-3 border-t border-slate-100 prb-reveal" data-testid="probation-setup">
      <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
        <div><p className="prb-metric-label mb-1">Duration</p>
          <Segmented testid="probation-period" value={mode} onChange={setMode} disabled={busy}
            options={[{ value: '6', label: '6 months' }, { value: '3', label: '3 months' }, { value: 'custom', label: 'Custom date' }]} /></div>
        {mode === 'custom' && <div className="prb-reveal"><p className="prb-metric-label mb-1">End date</p>
          <input type="date" className="prb-input w-[160px]" value={custom} onChange={e => setCustom(e.target.value)} data-testid="probation-custom-date" /></div>}
        {p && <div className="flex-1 min-w-[180px]"><p className="prb-metric-label mb-1">Reason</p>
          <input className="prb-input" placeholder="Optional — why are the dates changing?" value={reason} onChange={e => setReason(e.target.value)} data-testid="probation-reason" /></div>}
        {end && <p className={`text-[12px] font-medium self-center ${past ? 'text-rose-600' : 'text-slate-500'}`} data-testid="probation-end-preview">Ends {fmtDate(end)}{past ? ' · in the past' : ''}</p>}
        <div className="flex gap-2 ml-auto">
          {p && <button type="button" className="prb-btn-ghost" onClick={() => setOpen(false)}>Cancel</button>}
          <button type="button" className="prb-btn-primary" disabled={busy || (mode === 'custom' && !custom) || (past && !confirmPast)} onClick={submit} data-testid="probation-save-btn">{p ? 'Save dates' : (past ? 'Open overdue review' : 'Start tracking')}</button>
        </div>
      </div>
      {past && (
        <label className="prb-banner mt-3 cursor-pointer select-none prb-reveal" data-testid="probation-past-confirm">
          <input type="checkbox" className="h-4 w-4 accent-violet-600" checked={confirmPast} onChange={e => setConfirmPast(e.target.checked)} data-testid="probation-past-confirm-checkbox" />
          <span className="text-[12.5px] text-slate-700"><span className="font-semibold text-rose-700">Period already ended — Open overdue review now.</span> The real dates are kept ({fmtDate(start)} → {fmtDate(end)}); the Reporting Manager review opens immediately as overdue and follows the normal HR approval workflow.</span>
        </label>
      )}
    </div>
  );
}

function ReviewerCard({ p, data, managers, suggest, canManage, busy, act, employeeId }) {
  const [sel, setSel] = useState('');
  const emp = data.employee;
  const current = managers.find(m => m.value === emp.manager_employee_id);
  const name = emp.manager_name || current?.label || null;
  const initials = (name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
  const assign = (id, reason) => act(() => axios.put(`${API}/api/hr/probation/employees/${employeeId}/reviewer`, { reviewer_employee_id: id, reason }, { headers: h() }), 'Reporting Manager updated').then(() => setSel(''));
  const s = suggest?.suggestion;
  return (
    <SectionCard icon={UserCheck} title="Reporting Manager" subtitle="Primary reviewer — separate from the Team Leader, never assigned automatically" testid="ws-probation-reviewer" delay={60} className="!p-4">
      <div className="prb-reviewer">
        <div className="flex items-center gap-3 min-w-0">
          <span className="prb-avatar">{initials}</span>
          <div className="min-w-0">
            <p className="text-[13.5px] font-semibold text-slate-900 truncate" data-testid="probation-reviewer-name">{name || 'Not assigned'}</p>
            <p className="text-[12px] text-slate-500 truncate">{emp.manager_employee_id || 'Assign a Reporting Manager to enable the review'}</p>
          </div>
        </div>
        {canManage && (
          <div className="prb-reviewer-controls">
            {s && !suggest.already_assigned && (
              <button type="button" className="prb-chip" disabled={busy} data-testid="probation-suggest-chip" onClick={() => assign(s.employee_id, `Accepted Team Leader suggestion (${suggest.team_name || 'team'})`)}>
                Use team leader · {s.full_name}
              </button>
            )}
            <select className="prb-input !h-8 w-[200px] text-[12.5px]" value={sel} onChange={e => setSel(e.target.value)} data-testid="probation-reviewer-select" disabled={busy}>
              <option value="">Change manager…</option>
              {managers.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <button type="button" className="prb-btn-ghost !h-8" disabled={busy || !sel} data-testid="probation-reviewer-save" onClick={() => assign(sel, 'Set by HR in Probation tab')}>Assign</button>
          </div>
        )}
      </div>
      {p?.verified === false && p.reviewer_flags?.includes('reviewer_missing') && (
        <p className="mt-2 text-[12px] text-amber-700" data-testid="probation-reviewer-hint">Flagged to HR: reminders stay disarmed until a Reporting Manager is assigned and verified.</p>
      )}
    </SectionCard>
  );
}
